import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { JianLogo } from '@/components/brand/jian-logo'
import { normalizeReturnUrl } from '@/features/auth/guards'
import { exchangeLaunchTicket } from '@/features/auth/services/auth-service'
import { useAuthSessionStore } from '@/features/auth/stores/auth-session-store'

type LaunchSearch = {
  ticket?: string
  redirect?: string
}

export const Route = createFileRoute('/launch')({
  validateSearch: (search: Record<string, unknown>): LaunchSearch => ({
    ticket: typeof search.ticket === 'string' ? search.ticket : undefined,
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  component: LaunchRoute,
})

function LaunchRoute() {
  const navigate = useNavigate()
  const { ticket, redirect } = Route.useSearch()
  const setSession = useAuthSessionStore((state) => state.setSession)
  const setInitialized = useAuthSessionStore((state) => state.setInitialized)
  const [error, setError] = useState<string | null>(null)
  const launchStartedRef = useRef(false)

  useEffect(() => {
    if (!ticket) {
      setError('缺少 launch ticket，无法完成主站登录桥接。')
      return
    }

    if (launchStartedRef.current) {
      return
    }

    launchStartedRef.current = true

    void (async () => {
      try {
        const session = await exchangeLaunchTicket(ticket)
        setSession(session)
        setInitialized(true)
        await navigate({ to: normalizeReturnUrl(redirect) })
      } catch (launchError) {
        launchStartedRef.current = false
        setError(launchError instanceof Error ? launchError.message : '桥接登录失败')
      }
    })()
  }, [navigate, redirect, setInitialized, setSession, ticket])

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
      <div className="w-full max-w-md rounded-3xl border border-border bg-card p-8 text-center shadow-sm">
        <div className="mb-6 flex justify-center">
          <JianLogo variant="full" size="md" />
        </div>
        {error ? (
          <div className="space-y-4">
            <h1 className="text-xl font-semibold">桥接登录失败</h1>
            <p className="text-sm leading-6 text-muted-foreground">{error}</p>
            <Button asChild className="w-full">
              <a href="/login">去登录页</a>
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <h1 className="text-xl font-semibold">正在接入主站登录状态</h1>
            <p className="text-sm leading-6 text-muted-foreground">
              请稍候，正在为你获取子站访问凭证并跳转到目标页面。
            </p>
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-primary/20 border-t-primary" />
          </div>
        )}
      </div>
    </div>
  )
}